import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Tabs,
  Tab,
} from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import axios from "axios";
import Swal from "sweetalert2";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-bootstrap-typeahead/css/Typeahead.css";
import API_BASE_URL from "../config";

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const Reports = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const storedCompany = localStorage.getItem("selectedCompany");
    return storedCompany ? JSON.parse(storedCompany) : null;
  });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ totalBookings: 0, totalRevenue: 0 });
  const [bookings, setBookings] = useState([]);
  const [view, setView] = useState("table");

  // Convert bookings to calendar events
  const events = bookings.map((b) => ({
    title: `${b.service_name} (${b.customer_name || "Unknown"})`,
    start: new Date(b.booking_date),
    end: new Date(new Date(b.booking_date).getTime() + 60 * 60 * 1000),
    allDay: false,
    resource: b,
  }));

  // Load companies and validate selectedCompany
  useEffect(() => {
    async function loadCompanies() {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const companyData = res.data || [];
        setCompanies(companyData);

        // Validate stored selectedCompany
        const storedCompany = localStorage.getItem("selectedCompany");
        if (storedCompany) {
          const parsedCompany = JSON.parse(storedCompany);
          const isValidCompany = companyData.some((c) => c.id === parsedCompany.id);
          if (!isValidCompany) {
            localStorage.removeItem("selectedCompany");
            setSelectedCompany(null);
          } else {
            setSelectedCompany(parsedCompany); // Ensure Typeahead reflects stored company
          }
        }
      } catch (err) {
        console.error("loadCompanies", err);
        Swal.fire("Error", "Failed to load companies", "error");
        setSelectedCompany(null);
      }
    }
    loadCompanies();
  }, []);

  // Auto-fetch reports when selectedCompany changes
  useEffect(() => {
    if (selectedCompany) {
      fetchReports();
    } else {
      setSummary({ totalBookings: 0, totalRevenue: 0 });
      setBookings([]);
    }
  }, [selectedCompany]);

  const fetchReports = async () => {
    if (!selectedCompany) {
      setSummary({ totalBookings: 0, totalRevenue: 0 });
      setBookings([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${selectedCompany.id}/reports`,
        { params: { from: fromDate, to: toDate } }
      );
      setSummary({
        totalBookings: res.data.totalBookings,
        totalRevenue: res.data.totalRevenue,
      });
      setBookings(res.data.bookings || []);
    } catch (err) {
      console.error("fetchReports", err);
      Swal.fire("Error", "Failed to fetch reports", "error");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-4">
      <h3 className="text-center mb-4 fw-bold">📊 Reports</h3>

      {/* Filters */}
      <Form className="mb-4">
        <Row className="g-3">
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label>Company</Form.Label>
              <Typeahead
                id="company-typeahead"
                labelKey="name"
                onChange={(selected) => {
                  setSelectedCompany(selected[0] || null);
                  localStorage.setItem("selectedCompany", JSON.stringify(selected[0] || {}));
                }}
                options={companies}
                placeholder="Search or select company..."
                selected={selectedCompany ? [selectedCompany] : []}
              />
            </Form.Group>
          </Col>

          <Col xs={6} md={3}>
            <Form.Group>
              <Form.Label>From</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Form.Group>
          </Col>

          <Col xs={6} md={3}>
            <Form.Group>
              <Form.Label>To</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={2} className="d-grid align-items-end">
            <Button
              onClick={fetchReports}
              variant="primary"
              className="mt-md-2"
              disabled={loading}
            >
              {loading ? <Spinner size="sm" animation="border" /> : "Apply"}
            </Button>
          </Col>
        </Row>
      </Form>

      {selectedCompany && (
        <>
          {/* Summary cards */}
          <Row className="gy-3 mb-4">
            <Col xs={12} md={6}>
              <Card className="shadow-sm text-center">
                <Card.Body>
                  <Card.Title>Total Bookings</Card.Title>
                  <Card.Text className="fs-3 fw-bold text-primary">
                    {summary.totalBookings}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card className="shadow-sm text-center">
                <Card.Body>
                  <Card.Title>Total Revenue</Card.Title>
                  <Card.Text className="fs-3 fw-bold text-success">
                    ₹{summary.totalRevenue}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tabs */}
          <Tabs
            activeKey={view}
            onSelect={(v) => setView(v)}
            className="mb-3 justify-content-center"
          >
            <Tab eventKey="table" title="Table View">
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Customer</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Service</th>
                        <th>Employee</th>
                        <th>Date</th>
                        <th>Amount (₹)</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-3">
                            No bookings found
                          </td>
                        </tr>
                      ) : (
                        bookings.map((b, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{b.customer_name}</td>
                            <td>{b.customer_email}</td>
                            <td>{b.customer_phone}</td>
                            <td>{b.service_name}</td>
                            <td>{b.employee_name}</td>
                            <td>
                              {new Date(b.booking_date).toLocaleString()}
                            </td>
                            <td>{b.amount}</td>
                            <td>{b.payment_status}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Tab>

            <Tab eventKey="calendar" title="Calendar View">
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <div style={{ height: "600px" }}>
                  <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: "100%" }}
                    views={["month", "week", "day"]}
                    popup
                    eventPropGetter={(event) => ({
                      style: {
                        backgroundColor:
                          event.resource.payment_status === "paid"
                            ? "#4caf50"
                            : "#f44336",
                        color: "white",
                      },
                    })}
                    onSelectEvent={(event) =>
                      Swal.fire({
                        title: "Booking Details",
                        html: `
                          <strong>Service:</strong> ${
                            event.resource.service_name
                          }<br/>
                          <strong>Customer:</strong> ${
                            event.resource.customer_name || "N/A"
                          }<br/>
                          <strong>Date:</strong> ${new Date(
                            event.resource.booking_date
                          ).toLocaleString()}<br/>
                          <strong>Amount:</strong> ₹${
                            event.resource.amount || "-"
                          }
                        `,
                        icon: "info",
                        confirmButtonText: "Close",
                      })
                    }
                  />
                </div>
              )}
            </Tab>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default Reports;